import { useState, useEffect } from 'react'
import s from './OpportunityByStartEndDate.scss'
import {
  makeReportsPostRequest,
  makeGetRequest,
  getReportsBaseUrl,
  makePatchRequest
} from 'utils/api'
import { toTeams } from 'utils/serializers'
import { toGanttBarChartData, toUsers, toCategories } from './serializers'
import { useLoading } from 'hooks'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { RangeDatePicker, DatePicker } from 'components/ideal-core-ui'
import { utcDate, timezoneDate, currencyFormatter } from 'utils/helpers'
import Select from 'react-select'
import { FaInfoCircle, FaFileExcel } from 'react-icons/fa'
import { UncontrolledTooltip } from 'reactstrap'
import Cookies from 'universal-cookie'
import { COOKIES } from 'utils/constants'
import moment from 'moment'
import currency from 'currency.js'
import { AddButton } from 'components/common/AddButton'
import { ExportCsv, ExportPdf } from '@material-table/exporters'
import { Gantt } from 'gantt-task-react'
import '!style-loader!css-loader!gantt-task-react/dist/index.css' // eslint-disable-line

const statuses = [
  { value: 'PROJECT_AND_WON', label: 'All Project and Won Opportunities' },
  { value: 'ACTIVE', label: 'All Active Opportunities' },
  { value: 'WON', label: 'All Won Opportunities' },
  { value: 'PROJECT', label: 'All Project Opportunities' },
  { value: 'ALL', label: 'All Opportunities' }
]

const TaskListColumn = ({ tasks }) => {
  return (
    <div>
      {tasks.map(item => {
        return (
          <div
            key={item.id}
            style={{
              height: 40,
              display: 'flex',
              alignItems: 'center',
              width: 200,
              marginRight: 10
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {item.name}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const TaskListHeader = () => {
  return <div style={{ height: 50 }} />
}

const TooltipContent = ({ task }) => {
  const duration = moment(task.end).diff(moment(task.start), 'days')
  const formattedStartDate = timezoneDate(task.start)
  const formattedEndDate = timezoneDate(task.end)

  return (
    <div
      style={{
        padding: '10px',
        borderRadius: '8px',
        backgroundColor: '#fff',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '300px',
        fontFamily: 'Arial, sans-serif',
        color: '#333'
      }}
    >
      <div
        style={{
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '8px',
          color: '#4A90E2'
        }}
      >
        {task.name}
      </div>
      <div
        style={{
          fontSize: '14px',
          color: '#555'
        }}
      >
        <strong>Start:</strong> {formattedStartDate}
      </div>
      <div
        style={{
          fontSize: '14px',
          color: '#555'
        }}
      >
        <strong>End:</strong> {formattedEndDate}
      </div>
      <div
        style={{
          fontSize: '14px',
          color: '#333'
        }}
      >
        <strong>Duration:</strong> {duration} {duration === 1 ? 'day' : 'days'}
      </div>
    </div>
  )
}

const cookies = new Cookies()

const OpportunityByStartEndDate = () => {
  const [pipelineInfo, setPipelineInfo] = useState(null)
  const [result, setResult] = useState({})
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [teamFilter, setTeamFilter] = useState([])
  const [statusFilter, setStatusFilter] = useState(statuses[0])
  const [scopeFilter, setScopeFilter] = useState([])
  const [teams, setTeams] = useState([])
  const [scopes, setScopes] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const setDateFilters = (dates, utc = true) => {
    setStartDate(utc ? utcDate(dates.from) : dates.from)
    setEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  const columns = [
    {
      title: 'Project Start Date',
      field: 'projectStartDate',
      defaultSort: 'asc',
      sorting: true,
      width: '200px',
      render: row => (row.projectStartDate ? timezoneDate(row.projectStartDate) : null),
      exportTransformer: row => (row.projectStartDate ? timezoneDate(row.projectStartDate) : null),
      emptyValue: '(empty)',
      editComponent: props => {
        return (
          <DatePicker
            keyProp={props.rowData.id}
            value={props.rowData.projectStartDate && timezoneDate(props.rowData.projectStartDate)}
            onChange={async value => {
              const date = value && value !== null ? utcDate(value) : ''
              await makePatchRequest(`/v1/deals/${props.rowData.id}`, {
                dealProjectStartDate: date
              })
              refreshReport()
            }}
            placeholder="Select Project Start Date"
          />
        )
      }
    },
    {
      title: 'Project End Date',
      field: 'projectEndDate',
      sorting: true,
      width: '200px',
      render: row => (row.projectEndDate ? timezoneDate(row.projectEndDate) : null),
      exportTransformer: row => (row.projectEndDate ? timezoneDate(row.projectEndDate) : null),
      emptyValue: '(empty)',
      editComponent: props => {
        return (
          <DatePicker
            keyProp={props.rowData.id}
            value={props.rowData.projectEndDate && timezoneDate(props.rowData.projectEndDate)}
            onChange={async value => {
              const date = value && value !== null ? utcDate(value) : ''
              await makePatchRequest(`/v1/deals/${props.rowData.id}`, {
                dealProjectEndDate: date
              })
              refreshReport()
            }}
            placeholder="Select Project End Date"
          />
        )
      }
    },
    {
      title: 'Scope(s)',
      field: 'dealScopes',
      sorting: true,
      width: '200px',
      editable: 'never',
      render: row => row.dealScopes.map(s => s.name).join(', '),
      exportTransformer: row => row.dealScopes.map(s => s.name).join(', ')
    },
    {
      title: 'Quote #',
      field: 'quoteNumber',
      sorting: true,
      editable: 'never',
      width: '200px'
    },
    {
      title: 'Project #',
      field: 'projectNumber',
      width: '200px',
      editable: 'never',
      sorting: true
    },
    {
      title: 'Opp Status',
      field: 'status',
      width: '200px',
      editable: 'never',
      sorting: true
    },
    {
      title: 'Opp Name',
      field: 'name',
      width: '200px',
      sorting: true,
      editable: 'never',
      render: row => (
        <a href={`/opportunities/${row.id}`} target="_blank" rel="noreferrer">
          {row.name}
        </a>
      )
    },
    {
      title: 'Client(s)',
      field: 'companies',
      sorting: true,
      width: '200px',
      editable: 'never',
      render: row => row.companies.join(', '),
      exportTransformer: row => row.companies.join(', ')
    },
    {
      title: 'Awarded To',
      field: 'awardedCompany',
      width: '200px',
      editable: 'never',
      sorting: true
    },
    {
      title: 'Bid Value',
      field: 'value',
      width: '200px',
      editable: 'never',
      sorting: true,
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.value}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.value })
    },
    {
      title: 'Cost',
      field: 'cost',
      editable: 'never',
      width: '200px',
      sorting: true,
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.cost}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.cost })
    },
    {
      title: 'Profit',
      editable: 'never',
      field: 'grossProfit',
      width: '200px',
      sorting: true,
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.grossProfit}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.grossProfit })
    },
    {
      title: 'Opp Manager',
      field: 'manager',
      editable: 'never',
      width: '200px',
      sorting: true
    }
  ]

  useEffect(() => {
    const sessionDateRangeFilters = cookies.get(COOKIES.DATE_RANGE_FILTERS)
    if (sessionDateRangeFilters?.startDate) {
      setDateFilters(
        {
          from: sessionDateRangeFilters.startDate,
          to: sessionDateRangeFilters.endDate
        },
        false
      )
    } else {
      setDateFilters({
        from: moment().subtract(2, 'weeks').startOf('day').format(),
        to: moment().add(6, 'weeks').endOf('day').format()
      })
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      const teams = await makeGetRequest('/v1/teams')
      setTeams(toTeams(teams))

      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))

      const scopes = await makeGetRequest('/v1/deal-scope')
      setScopes(toCategories(scopes))
    })()
  }, [])

  async function refreshReport() {
    if (!startDate || !endDate) return
    const result = await withLoadingLocks(
      makeReportsPostRequest('/v1/deals/project-dates', {
        from: startDate,
        to: endDate,
        ...(userFilter && userFilter.length
          ? { userFilter: userFilter.map(user => user.value) }
          : {}),
        ...(teamFilter && teamFilter.length
          ? { teamFilter: teamFilter.map(user => user.value) }
          : {}),
        ...(scopeFilter && scopeFilter.length
          ? { dealScopes: scopeFilter.map(user => user.value) }
          : {}),
        ...(statusFilter ? { statusGroup: statusFilter.value } : {})
      })
    )
    setResult(result)
    setPipelineInfo(toGanttBarChartData(result))
  }

  useEffect(() => {
    refreshReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, teamFilter, userFilter, statusFilter, scopeFilter])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/deals/project-dates/export?from=${startDate}&to=${endDate}&userFilter=${userFilter
          .map(user => user.value)
          .join()}&teamFilter=${teamFilter.map(user => user.value).join()}&dealScopes=${scopeFilter.map(user => user.value).join()}&statusGroup=${statusFilter.value}&token=${getToken()}`
      ),
      '_blank'
    )
  }

  return (
    <>
      <h6 className={s.title}>Sales Reports &gt; Opportunities by Start and End Dates</h6>
      <div className={s.filters}>
        <RangeDatePicker startDate={startDate} endDate={endDate} onChange={setDateFilters} />
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type2" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type2">
          To see all opportunities, please remove all teams from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setStatusFilter(values)}
          options={statuses}
          value={statusFilter}
          placeholder="Select Status"
          className={s.userSelect}
        />
        <Select
          onChange={values => setScopeFilter(values)}
          options={scopes}
          value={scopeFilter}
          placeholder="Select Scopes(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type3" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type3">
          To see all opportunities, please remove all scopes from selection.
        </UncontrolledTooltip>
      </div>
      {result.deals?.length > 0 ? (
        <>
          <h6 className={s.subtitle}>
            Total: {result?.count ?? 0} opportunities &#8226; Bid:{' '}
            {currency(result?.totalBidValue ?? 0, { symbol: getCurrency().symbol }).format()}
          </h6>
          {pipelineInfo.length > 0 && (
            <div className={s.barChart}>
              <Gantt
                tasks={pipelineInfo}
                viewDate={moment(startDate).toDate()}
                viewMode="Month"
                columnWidth={100}
                TooltipContent={TooltipContent}
                rowHeight={40}
                TaskListHeader={TaskListHeader}
                TaskListTable={TaskListColumn}
              />
            </div>
          )}
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            <MaterialTable
              title=""
              columns={columns}
              data={result.deals}
              options={{
                tableLayout: 'fixed',
                sorting: true,
                headerStyle: {
                  backgroundColor: '#eee',
                  color: '#000',
                  fontSize: '14px'
                },
                pageSize: 10,
                padding: 'dense',
                pageSizeOptions: [5, 10, 20, 50, { value: result.deals.length, label: 'All' }],
                emptyRowsWhenPaging: false,
                exportMenu: [
                  {
                    label: 'Export PDF',
                    exportFunc: (cols, datas) =>
                      ExportPdf(cols, datas, 'Opportunities by Project Start and End Dates')
                  },
                  {
                    label: 'Export CSV',
                    exportFunc: (cols, datas) =>
                      ExportCsv(cols, datas, 'Opportunities by Project Start and End Dates')
                  }
                ],
                columnsButton: true
              }}
              style={{
                boxShadow: 'none',
                fontSize: '13px'
              }}
              cellEditable={{
                onCellEditApproved: () => {
                  return new Promise(resolve => resolve())
                }
              }}
            />
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try different filters.</p>
      )}
    </>
  )
}

export default OpportunityByStartEndDate
